import './ExploreMapContainer.css';

import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Button from '@mui/joy/Button';
import { Cities, IVenue } from 'btdt-types';
import ExploreMap from './ExploreMap/ExploreMap';
import SiteLogo from './SiteLogo/SiteLogo';
import CityFilter from '../CityFilter/CityFilter';
import FiltersBar from './FiltersBar/FiltersBar';
import { ExploreScreenContext } from '../context';
import { NavigationContext, NavigationFlows } from '../../context';
import { Events, trackEvent } from '../../utils/analytics';
import { getVenueUrl } from '../../helpers/navigation';
import { useDevices } from '../../helpers/devices';
import { VenuePreviewPane } from './VenuePreviewPane/VenuePreviewPane';
import AddVenueModal from '../../AddVenueForm/AddVenueModal';

const MAPBOX_TOKEN = 'pk.eyJ1IjoiYW1pcm96ZXIiLCJhIjoiY2xiODN3OHp5MDgzdzNvbXRzbHRmNDh6byJ9.z32MJnbhRaPU0qEXgjq7bQ';
const ENABLE_MOBILE_PREVIEWS = false;

const ExploreMapContainer: React.FC = () => {
  const { setFlow } = React.useContext(NavigationContext);
  const { filters, applyFilters, venues } = React.useContext(ExploreScreenContext);
  const [mobilePreviewedVenueId, setMobilePreviewedVenueId] = React.useState<number | null>(null);

  // Add venue state
  const [addVenueMode, setAddVenueMode] = React.useState(false);
  const [pinCoordinates, setPinCoordinates] = React.useState<[number, number] | null>(null);
  const [pinAddress, setPinAddress] = React.useState('');
  const [showAddVenueModal, setShowAddVenueModal] = React.useState(false);

  const { isMobile } = useDevices();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: idRouteParam } = useParams();

  const highlightVenueId = React.useMemo(() => {
    if (isMobile && ENABLE_MOBILE_PREVIEWS) {
      return mobilePreviewedVenueId;
    }
    return idRouteParam ? parseInt(idRouteParam) : null;
  }, [isMobile, mobilePreviewedVenueId, idRouteParam]);

  React.useEffect(() => {
    setFlow(NavigationFlows.EXPLORE);
  }, []);

  const handleCityChange = (city: Cities): void => {
    applyFilters({ ...filters, city });
    setMobilePreviewedVenueId(null);
    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  const handleVenueClick = (venue: IVenue): void => {
    if (addVenueMode) return;
    trackEvent(Events.CLICK_EXPLORE_MAP_MARKER);
    if (isMobile && ENABLE_MOBILE_PREVIEWS) {
      setMobilePreviewedVenueId(venue.id);
    } else {
      navigate(getVenueUrl(venue));
    }
  };

  const handlePinDrop = async (coords: [number, number]) => {
    setPinCoordinates(coords);
    setShowAddVenueModal(true);
    // Reverse geocode
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${coords[1]},${coords[0]}.json?access_token=${MAPBOX_TOKEN}&language=he`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        setPinAddress(data.features[0].place_name || '');
      }
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
    }
  };

  const handleAddVenueAddressChange = (newAddress: string, coords: [number, number]) => {
    setPinAddress(newAddress);
    setPinCoordinates(coords);
  };

  const resetAddVenueState = () => {
    setAddVenueMode(false);
    setPinCoordinates(null);
    setPinAddress('');
    setShowAddVenueModal(false);
  };

  const handleAddVenueClick = () => {
    setAddVenueMode(true);
  };

  return (
    <div className="ExploreMapContainer">
      <ExploreMap
        city={filters.city}
        venues={venues}
        onViewBoundsChange={(bounds) => applyFilters({ ...filters, bounds })}
        onVenueClick={handleVenueClick}
        highlightVenueId={highlightVenueId}
        pinDropMode={addVenueMode}
        pinDropCoordinates={pinCoordinates}
        onPinDrop={handlePinDrop}
      />
      <div className="ExploreMapContainer-city-filter-container">
        <SiteLogo />
        <CityFilter value={filters.city} onChange={handleCityChange} />
      </div>
      <div className="ExploreMapContainer-add-venue-container">
        {addVenueMode ? (
          <div className="ExploreMapContainer-add-venue-instruction">
            <span>לחצו על המפה כדי לסמן את המיקום</span>
            <Button type="button" size="sm" variant="outlined" color="neutral" onClick={resetAddVenueState}>ביטול</Button>
          </div>
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={handleAddVenueClick}
          >
            + הוספת מקום
          </Button>
        )}
      </div>
      <div className="ExploreMapContainer-filters-bar-container">
        <FiltersBar filters={filters} applyFilters={applyFilters} />
      </div>
      {mobilePreviewedVenueId && ENABLE_MOBILE_PREVIEWS && <VenuePreviewPane venueId={mobilePreviewedVenueId} />}
      {showAddVenueModal && pinCoordinates && (
        <AddVenueModal
          open={showAddVenueModal}
          coordinates={pinCoordinates}
          address={pinAddress}
          city={filters.city}
          onAddressChange={handleAddVenueAddressChange}
          onClose={resetAddVenueState}
          onSuccess={resetAddVenueState}
        />
      )}
    </div>
  );
};

export default ExploreMapContainer;
